import type { InjectedRouter } from 'js/lib/connectToRouter';

import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import { getCourseHomeLinkBySlug } from 'bundles/enterprise-legacy-learner-home/utils/courseAndS12nUtils';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';

export type EnrollmentRoutingArgs = {
  courseId: string;
  s12nId?: string;
  slug?: string;
  course?: CoursesV1;
  isEnrolled: boolean;
  isSpecialization?: boolean;
  isS12nUpgradeEligible?: boolean;
  isLegalConsolidationFlowAndHasTouQueryParam: boolean;
  shouldCheckActionParams?: boolean;
  shouldCheckProductIdParam?: boolean;
  lastActiveCourseSlug?: string;
  router: InjectedRouter;
};

export type EnrollmentRouting = {
  shouldAutoOpenEnrollOnMount: boolean;
  courseHomeRedirectUrl?: string;
};

/**
 * Resolves the two pieces of enroll-routing business logic that don't depend on the
 * modal/tracking machinery: whether the `?action=enroll` deep link should
 * auto-open enrollment on mount, and the course-home URL that an already-enrolled
 * or upgrade-eligible learner should be redirected to. Everything else (the click
 * handlers, modal toggles, tracking, and auth redirects) lives in withEnrollModal.
 */
export const getEnrollmentRouting = ({
  courseId,
  s12nId,
  slug,
  course,
  isEnrolled,
  isSpecialization,
  isS12nUpgradeEligible = false,
  isLegalConsolidationFlowAndHasTouQueryParam,
  shouldCheckActionParams,
  shouldCheckProductIdParam,
  lastActiveCourseSlug,
  router,
}: EnrollmentRoutingArgs): EnrollmentRouting => {
  const { action, productId } = router.location.query;

  const hasValidProductIdParam =
    shouldCheckProductIdParam &&
    ((isSpecialization && s12nId === productId) || (!isSpecialization && courseId === productId));
  const hasValidEnrollQueryParams = (hasValidProductIdParam || shouldCheckActionParams) && action === ENROLL;

  const shouldAutoOpenEnrollOnMount = Boolean(
    !isEnrolled && !isLegalConsolidationFlowAndHasTouQueryParam && hasValidEnrollQueryParams
  );

  const getCourseHomeRedirectUrl = (): string | undefined => {
    if (isSpecialization && course && isS12nUpgradeEligible) {
      // For users on SDP and enrolled in an older version, upgrading should take priority over switching subscriptions.
      // The primary button will be "Go to course"
      return getCourseHomeLinkBySlug(lastActiveCourseSlug ?? course.slug);
    }
    if (isEnrolled && isSpecialization && course && !course.isPreEnroll) {
      return getCourseHomeLinkBySlug(course.slug);
    }
    if (isEnrolled && typeof slug === 'string') {
      return getCourseHomeLinkBySlug(slug);
    }
    return undefined;
  };

  return {
    shouldAutoOpenEnrollOnMount,
    courseHomeRedirectUrl: getCourseHomeRedirectUrl(),
  };
};

export default getEnrollmentRouting;
